import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
	FiEdit3,
	FiFile,
} from "react-icons/fi";
import { documentsService } from "services";
import DocumentAttachmentActionsMenu, {
	downloadStoreFolderAttachment,
	enrichAttachmentsWithStoreFolderIds,
} from "./DocumentAttachmentActionsMenu";
import { useTranslation } from "react-i18next";

/** Slug de ruta para la tabla de documentos pendientes de firma (`/documents/<slug>/`). */
export const DOCUMENTS_PENDING_SIGNATURES_SLUG = "pending-signatures";
export const documentsPendingSignaturesUrl = () =>
	`/documents/${DOCUMENTS_PENDING_SIGNATURES_SLUG}/`;

const FILE_EXTENSION_ICONS = {
	pdf: { color: "#E53935", label: "PDF" },
	doc: { color: "#1565C0", label: "DOC" },
	docx: { color: "#1565C0", label: "DOCX" },
	xls: { color: "#2E7D32", label: "XLS" },
	xlsx: { color: "#2E7D32", label: "XLSX" },
	ppt: { color: "#E65100", label: "PPT" },
	pptx: { color: "#E65100", label: "PPTX" },
	jpg: { color: "#00897B", label: "JPG" },
	jpeg: { color: "#00897B", label: "JPEG" },
	png: { color: "#00897B", label: "PNG" },
	gif: { color: "#00897B", label: "GIF" },
	txt: { color: "#546E7A", label: "TXT" },
	csv: { color: "#2E7D32", label: "CSV" },
	zip: { color: "#6D4C41", label: "ZIP" },
	rar: { color: "#6D4C41", label: "RAR" },
};

const getFileExtension = (filename) => {
	if (!filename) return "";
	const parts = filename.split(".");
	return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const FileExtensionIcon = ({ filename }) => {
	const ext = getFileExtension(filename);
	const config = FILE_EXTENSION_ICONS[ext] || { color: "#78909C", label: ext.toUpperCase() || "FILE" };

	return (
		<div className="file-ext-icon" style={{ backgroundColor: config.color }}>
			<span>{config.label}</span>
		</div>
	);
};

const formatFileSize = (sizeInBytes) => {
	if (!sizeInBytes) return "0 kb";
	const kb = sizeInBytes / 1024;
	return `${kb.toFixed(2).replace(".", ",")} kb`;
};

const formatDate = (dateString) => {
	if (!dateString) return "";
	const date = new Date(dateString);
	return date.toLocaleDateString("es-ES", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

const parseAttachmentList = (response) => {
	const list = response?.data || response || [];
	return Array.isArray(list) ? list : [];
};

const fetchAndEnrichDocumentNotifications = async (employeeId) => {
	const [pendingRes, recentRes, foldersRes] = await Promise.all([
		documentsService.getPendingSignatures(employeeId),
		documentsService.getRecentAttachments(employeeId),
		documentsService.getStoreFolders(),
	]);

	let folders = foldersRes?.data || foldersRes || [];
	if (!Array.isArray(folders)) {
		folders = [];
	}

	const pending = parseAttachmentList(pendingRes);
	const recent = parseAttachmentList(recentRes);

	const [enrichedPending, enrichedRecent] = await Promise.all([
		enrichAttachmentsWithStoreFolderIds(pending, folders, employeeId),
		enrichAttachmentsWithStoreFolderIds(recent, folders, employeeId),
	]);

	return { pending: enrichedPending, recent: enrichedRecent };
};

// ── Context para compartir datos entre las dos secciones ──

const DocumentsNotificationsContext = createContext({
	pendingSignatures: [],
	recentAttachments: [],
	loading: true,
	refetchNotifications: () => {},
});

const DocumentsNotificationsProviderInner = ({ user, children }) => {
	const [pendingSignatures, setPendingSignatures] = useState([]);
	const [recentAttachments, setRecentAttachments] = useState([]);
	const [loading, setLoading] = useState(true);

	const refetchNotifications = useCallback(async () => {
		if (!user?.id) {
			setPendingSignatures([]);
			setRecentAttachments([]);
			return;
		}
		try {
			const { pending, recent } = await fetchAndEnrichDocumentNotifications(user.id);
			setPendingSignatures(pending);
			setRecentAttachments(recent);
		} catch (error) {
			console.error("Error fetching document notifications:", error);
		}
	}, [user?.id]);

	useEffect(() => {
		let isMounted = true;
		(async () => {
			if (!user?.id) {
				setPendingSignatures([]);
				setRecentAttachments([]);
				setLoading(false);
				return;
			}
			setLoading(true);
			try {
				const { pending, recent } = await fetchAndEnrichDocumentNotifications(user.id);

				if (!isMounted) return;

				setPendingSignatures(pending);
				setRecentAttachments(recent);
			} catch (error) {
				console.error("Error fetching document notifications:", error);
			} finally {
				if (isMounted) setLoading(false);
			}
		})();
		return () => {
			isMounted = false;
		};
	}, [user?.id]);

	return (
		<DocumentsNotificationsContext.Provider
			value={{ pendingSignatures, recentAttachments, loading, refetchNotifications }}
		>
			{children}
		</DocumentsNotificationsContext.Provider>
	);
};

const mapStateToProps = (reducers) => ({
	user: reducers.authReducer.user,
});

export const DocumentsNotificationsProvider = connect(mapStateToProps)(DocumentsNotificationsProviderInner);

// ── Cards de notificación (van ARRIBA de las carpetas) ──

export const PendingSignatureCards = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { pendingSignatures, recentAttachments, loading } = useContext(DocumentsNotificationsContext);

	if (loading) return null;

	const hasPending = pendingSignatures.length > 0;
	const hasRecent = recentAttachments.length > 0;

	if (!hasPending && !hasRecent) return null;

	return (
		<div className="documents-notification-cards">
			{hasPending && (
				<div className="notification-card">
					<div className="notification-card-left">
						<FiEdit3 className="notification-icon" />
						<span className="notification-text"
							dangerouslySetInnerHTML={{
								__html: t("documents.pendingSignaturesCount", { count: pendingSignatures.length })
									.replace(/(\d+)/, "<strong>$1</strong>")
							}}
						/>
					</div>
					<button
						type="button"
						className="notification-link"
						onClick={() => history.push(documentsPendingSignaturesUrl())}
					>
						{t("documents.viewDocuments")}
					</button>
				</div>
			)}
			{hasRecent && (
				<div className="notification-card">
					<div className="notification-card-left">
						<FiFile className="notification-icon" />
						<span className="notification-text"
							dangerouslySetInnerHTML={{
								__html: t("documents.recentDocumentsCount", { count: recentAttachments.length })
									.replace(/(\d+)/, "<strong>$1</strong>")
							}}
						/>
					</div>
					<a className="notification-link" href="#recent-documents">
						{t("documents.viewDocuments")}
					</a>
				</div>
			)}
		</div>
	);
};

// ── Sección de documentos recientes (va DEBAJO de las carpetas) ──

export const RecentDocumentsList = ({ folders = [] }) => {
	const { t } = useTranslation();
	const { recentAttachments, loading, refetchNotifications } = useContext(DocumentsNotificationsContext);

	if (loading || recentAttachments.length === 0) return null;

	return (
		<div className="recent-documents-section" id="recent-documents">
			<h3 className="recent-documents-title">{t("documents.recentDocuments")}</h3>
			<div className="recent-documents-list">
				{recentAttachments.map((attachment, index) => (
					<div className="recent-document-row" key={attachment.url || attachment.uuid || index}>
						<div className="recent-document-info">
							<FileExtensionIcon filename={attachment.name} />
							<div className="recent-document-details">
								<button
									type="button"
									className="recent-document-name"
									onClick={() => downloadStoreFolderAttachment(attachment, undefined, folders, t)}
								>
									{attachment.name}
								</button>
								<span className="recent-document-meta">
									{formatFileSize(attachment.size)} · {t("documents.createdOn", { date: formatDate(attachment.created) })}
									{attachment.folder_name && (
										<> · <span className="recent-document-folder">{attachment.folder_name}</span></>
									)}
								</span>
							</div>
						</div>
						<div className="recent-document-actions">
							<DocumentAttachmentActionsMenu
								attachment={attachment}
								foldersList={folders}
								onAfterMutation={refetchNotifications}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
